import { Page, Text, View, Document, Image } from '@react-pdf/renderer';
import { DateTime } from 'luxon';
import React from 'react';

import { IPrintCollectOccurrencesResponse } from '../../../types/PrintOccurrences';
import { styles } from './styled';

interface ICollectOccurrencesDocumentProps {
  data: IPrintCollectOccurrencesResponse['data'];
  logo?: string;
  username?: string;
}

const formatDate = (dateStr: string) => {
  if (!dateStr) return '';
  const dt = DateTime.fromISO(dateStr);
  return dt.isValid ? dt.toFormat('dd/MM/yyyy') : dateStr;
};

export const CollectOccurrencesDocument: React.FC<
  ICollectOccurrencesDocumentProps
> = ({ data, logo, username }) => {
  const { cabecalho, linha_do_tempo } = data;

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.header} fixed>
          <View style={styles.headerTop}>
            <View style={styles.logoContainer}>
              {logo && <Image style={styles.logoImage} src={logo} />}
            </View>
            <View style={styles.infoCenter}>
              <Text style={[styles.titleHeader, styles.labelBold]}>
                RELATÓRIO DE OCORRÊNCIAS
              </Text>
              <Text style={[styles.name, styles.labelBold]}>
                {cabecalho.NOME_EMPRESA}
              </Text>
            </View>
            <View style={styles.metaDataHeaderWrapper}>
              <View style={styles.metaDataHeader}>
                <Text>Data....: {DateTime.local().toFormat('dd/MM/yyyy')}</Text>
                <Text>Hora....: {DateTime.local().toFormat('HH:mm')}</Text>
                <Text>Usuário: {username}</Text>
              </View>
            </View>
          </View>
          <View style={styles.metaDataContainer}>
            <View style={styles.metaDataColumnLeft}>
              <Text style={styles.metaDataText}>Rota: {cabecalho.ROTA}</Text>
              <Text style={styles.metaDataText}>
                Motorista: {cabecalho.CARRETEIRO}
              </Text>
              <Text style={styles.metaDataText}>Linha: {cabecalho.LINHA}</Text>
              <Text style={styles.metaDataText}>
                Saída: {formatDate(cabecalho.DATA_SAIDA)}
              </Text>
            </View>
            <View style={styles.metaDataColumnRight}>
              <Text style={styles.metaDataText}>
                Veículo: {cabecalho.VEICULO}
              </Text>
              <Text style={styles.metaDataText}>
                Cód. Coleta: {cabecalho.COLETA}
              </Text>
              <Text style={styles.metaDataText}>
                CNPJ: {cabecalho.CNPJ_EMPRESA}
              </Text>
            </View>
          </View>
        </View>

        {linha_do_tempo.length === 0 ? (
          <Text style={{ textAlign: 'center', marginTop: 20 }}>
            Nenhuma ocorrência registrada para esta coleta.
          </Text>
        ) : (
          linha_do_tempo.map((item, index) => (
            <View
              key={item.DFIDREGISTRO || index}
              style={styles.occurrenceBlock}
              wrap={false}
            >
              <View style={styles.occurrenceHeader}>
                <Text style={styles.occurrenceTitle}>
                  {item.DFTIPOREGISTRO === '1' && 'Odômetro Inicial'}
                  {item.DFTIPOREGISTRO === '2' && 'Odômetro Final'}
                  {item.DFTIPOREGISTRO !== '1' &&
                    item.DFTIPOREGISTRO !== '2' &&
                    `Produtor: ${item.NOME_PRODUTOR}`}
                </Text>
                <Text style={styles.headerText}>
                  {item.DFHORACOLETA ? `Hora Coleta: ${item.DFHORACOLETA}` : ''}
                </Text>
              </View>

              <View style={styles.grid}>
                {item.DFTIPOREGISTRO !== '1' && item.DFTIPOREGISTRO !== '2' && (
                  <>
                    <View style={styles.gridItem}>
                      <Text style={styles.label}>Fazenda:</Text>
                      <Text style={styles.value}>{item.NOME_FAZENDA}</Text>
                    </View>
                    <View style={styles.gridItem}>
                      <Text style={styles.label}>Tanque:</Text>
                      <Text style={styles.value}>{item.DFDESCTANQUE}</Text>
                    </View>
                    <View style={styles.gridItem}>
                      <Text style={styles.label}>Qtd. Coletada:</Text>
                      <Text style={styles.value}>
                        {item.DFQTDCOLETADA != null
                          ? `${item.DFQTDCOLETADA}L`
                          : '-'}
                      </Text>
                    </View>
                    <View style={styles.gridItem}>
                      <Text style={styles.label}>Temperatura:</Text>
                      <Text style={styles.value}>
                        {item.DFTEMPERATURA != null
                          ? `${item.DFTEMPERATURA}ºC`
                          : '-'}
                      </Text>
                    </View>
                    <View style={styles.gridItem}>
                      <Text style={styles.label}>
                        Alizarol ({item.DFTIPOALIZAROL || '-'}):
                      </Text>
                      <Text style={styles.value}>{item.DFALIZAROL}</Text>
                    </View>
                  </>
                )}
                <View style={styles.gridItem}>
                  <Text style={styles.label}>Data/Hora Registro:</Text>
                  <Text style={styles.value}>
                    {item.DFDATAREGISTRO ? formatDate(item.DFDATAREGISTRO) : ''}{' '}
                    {item.DFHORAREGISTRO}
                  </Text>
                </View>
                {item.DFOBSERVACAO && (
                  <View style={{ width: '100%', marginTop: 4 }}>
                    <Text style={styles.label}>Observação:</Text>
                    <Text style={styles.value}>{item.DFOBSERVACAO}</Text>
                  </View>
                )}
              </View>

              {item.foto && (
                <View style={styles.photoContainer}>
                  <Image src={item.foto} style={styles.photo} />
                </View>
              )}
            </View>
          ))
        )}

        <Text
          style={styles.footer}
          render={({ pageNumber, totalPages }) =>
            `Página ${pageNumber} de ${totalPages}`
          }
          fixed
        />
      </Page>
    </Document>
  );
};
